import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

/**
 * TimelineMemoryCard - Memory card on the timeline that navigates to detail view
 */
function TimelineMemoryCard({ memory, onView }) {
  const navigate = useNavigate();

  const {
    id,
    title,
    year,
    voice_url,
    voice_duration,
    text,
    photo_urls,
    life_event_id,
  } = memory;

  // Photo count
  const photoCount = photo_urls?.length || 0;

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Build description
  const parts = [];
  if (year) parts.push(`~${year}`);
  if (voice_url) parts.push(`🎙 ${formatDuration(voice_duration) || 'Voice'}`);
  if (photoCount > 0) parts.push(`${photoCount} photo${photoCount !== 1 ? 's' : ''}`);
  if (text && !voice_url) parts.push('Written story');

  const handleClick = () => {
    onView?.(memory);
    // Navigate to story view if linked, otherwise to memory detail view
    if (life_event_id) {
      navigate(`/stories/${life_event_id}`);
    } else {
      navigate(`/my-life/memories/${id}`);
    }
  };

  return (
    <div className="flex items-start gap-4 pl-4">
      {/* Timeline dot - small for memories */}
      <div
        className="
          w-3 h-3 rounded-full
          bg-gray-300
          flex-shrink-0 mt-4
        "
      />

      {/* Connector line */}
      <div className="w-8 h-px bg-gray-200 mt-5 flex-shrink-0" />

      {/* Card */}
      <div
        className="
          flex-1 bg-white rounded-lg py-3 px-4
          border border-gray-100
          hover:border-gray-200 hover:shadow-sm cursor-pointer
          transition-all duration-200
        "
        onClick={handleClick}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-sans text-sm text-gray-800">
              {title ? `"${title}"` : 'Untitled memory'}
            </p>
            <p className="font-sans text-xs text-gray-500">
              Memory{parts.length > 0 ? ` · ${parts.join(' · ')}` : ''}
            </p>
          </div>
          <span className="text-fb-olive text-xs">View →</span>
        </div>
      </div>
    </div>
  );
}

TimelineMemoryCard.propTypes = {
  memory: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    year: PropTypes.number,
    voice_url: PropTypes.string,
    voice_duration: PropTypes.number,
    text: PropTypes.string,
    photo_urls: PropTypes.array,
    life_event_id: PropTypes.number,
  }).isRequired,
  onView: PropTypes.func,
};

TimelineMemoryCard.defaultProps = {
  onView: null,
};

export default TimelineMemoryCard;
